import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNotifications = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    patient_id: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/notifications`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const patientName = patients.find(p => p.id === formData.patient_id)?.name;
      toast.success(`Notification sent to ${patientName}`);
      
      setFormData({ patient_id: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('Failed to send notification');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-notifications-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Send Notifications
        </h1>
        <p className="text-[#6B7280]">Send messages and notifications to patients</p>
      </div>

      <div className="max-w-2xl">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Select Patient
              </label>
              <select
                value={formData.patient_id}
                onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                data-testid="patient-select"
              >
                <option value="">Choose a patient...</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name} - {patient.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Subject
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="Appointment Reminder, Payment Due, etc."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                data-testid="subject-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows="6"
                placeholder="Type your message to the patient..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                data-testid="message-input"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center transition-all shadow-lg"
              data-testid="send-notification-button"
            >
              <Send className="w-5 h-5 mr-2" />
              Send Notification
            </button>
          </form>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-[#1F2937] mb-2">How Notifications Work</h3>
          <ul className="text-sm text-[#6B7280] space-y-2">
            <li>• Patients can view their notifications in the patient portal</li>
            <li>• Notifications are stored in the system for record keeping</li>
            <li>• Use this for appointment reminders, payment notices, or general updates</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications;
